export const SERVICE_INFO = {
  // Delivery window metadata used by the rates panel for display and ranking.
  //
  // - minDeliveryDays / maxDeliveryDays: define the delivery window.
  //   These are the single source of truth — translation strings use {min}/{max}
  //   placeholders that are filled from these values at render time.
  //
  // - deliveryTimeRank (optional): a manual tiebreaker used ONLY when two services
  //   share the same min/max but differ in time-of-day commitment.
  //   Lower = faster. Only needed for services like:
  //     FedEx Priority Overnight (by 10:30 am) vs Standard Overnight (by 3-5 pm)
  //   When omitted, rank is computed automatically as (min * 2) + max.
  //
  // Prefer API-provided timing fields when available on the returned rate object.

  // USPS
  usps_media: {
    deliveryWindow: 'SERVICE_USPS_MEDIA_WINDOW',
    description: 'SERVICE_USPS_MEDIA_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 8,
  },
  usps_bound_printed_matter: {
    deliveryWindow: 'SERVICE_USPS_BOUND_PRINTED_MATTER_WINDOW',
    description: 'SERVICE_USPS_BOUND_PRINTED_MATTER_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 8,
  },
  usps_ground_advantage: {
    deliveryWindow: 'SERVICE_USPS_GROUND_ADVANTAGE_WINDOW',
    description: 'SERVICE_USPS_GROUND_ADVANTAGE_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 5,
  },
  usps_firstclass: {
    deliveryWindow: 'SERVICE_USPS_FIRSTCLASS_WINDOW',
    description: 'SERVICE_USPS_FIRSTCLASS_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 5,
  },
  usps_priority: {
    deliveryWindow: 'SERVICE_USPS_PRIORITY_WINDOW',
    description: 'SERVICE_USPS_PRIORITY_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 3,
  },
  usps_priority_express: {
    deliveryWindow: 'SERVICE_USPS_PRIORITY_EXPRESS_WINDOW',
    description: 'SERVICE_USPS_PRIORITY_EXPRESS_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 3,
  },
  usps_priority_international: {
    deliveryWindow: 'SERVICE_USPS_PRIORITY_INTERNATIONAL_WINDOW',
    description: 'SERVICE_USPS_PRIORITY_INTERNATIONAL_DESC',
    minDeliveryDays: 6,
    maxDeliveryDays: 10,
  },
  usps_express_international: {
    deliveryWindow: 'SERVICE_USPS_EXPRESS_INTERNATIONAL_WINDOW',
    description: 'SERVICE_USPS_EXPRESS_INTERNATIONAL_DESC',
    minDeliveryDays: 3,
    maxDeliveryDays: 5,
  },
  // Display says "Varies by destination" — min/max here are ranking-only estimates
  usps_first_package_international: {
    deliveryWindow: 'SERVICE_USPS_FIRST_PACKAGE_INTERNATIONAL_WINDOW',
    description: 'SERVICE_USPS_FIRST_PACKAGE_INTERNATIONAL_DESC',
    minDeliveryDays: 7,
    maxDeliveryDays: 14,
  },

  // UPS
  ups_mi_expedited: {
    deliveryWindow: 'SERVICE_UPS_MI_EXPEDITED_WINDOW',
    description: 'SERVICE_UPS_MI_EXPEDITED_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 7,
  },
  ups_ground: {
    deliveryWindow: 'SERVICE_UPS_GROUND_WINDOW',
    description: 'SERVICE_UPS_GROUND_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 5,
  },
  ups_2day_air: {
    deliveryWindow: 'SERVICE_UPS_2DAY_AIR_WINDOW',
    description: 'SERVICE_UPS_2DAY_AIR_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 2,
  },
  ups_3day_select: {
    deliveryWindow: 'SERVICE_UPS_3DAY_SELECT_WINDOW',
    description: 'SERVICE_UPS_3DAY_SELECT_DESC',
    minDeliveryDays: 3,
    maxDeliveryDays: 3,
  },
  // AM variant delivers by morning — rank 1 to beat standard 2nd Day Air (rank 2)
  ups_2day_air_am: {
    deliveryWindow: 'SERVICE_UPS_2DAY_AIR_AM_WINDOW',
    description: 'SERVICE_UPS_2DAY_AIR_AM_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 2,
    deliveryTimeRank: 1,
  },
  // Saver delivers by end-of-day — rank 3 to lose to standard Next Day Air (rank 2)
  ups_next_air_saver: {
    deliveryWindow: 'SERVICE_UPS_NEXT_AIR_SAVER_WINDOW',
    description: 'SERVICE_UPS_NEXT_AIR_SAVER_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 1,
    deliveryTimeRank: 3,
  },
  // Standard Next Day Air delivers by 10:30 am
  ups_next_air: {
    deliveryWindow: 'SERVICE_UPS_NEXT_AIR_WINDOW',
    description: 'SERVICE_UPS_NEXT_AIR_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 1,
    deliveryTimeRank: 2,
  },
  ups_ground_saver: {
    deliveryWindow: 'SERVICE_UPS_GROUND_SAVER_WINDOW',
    description: 'SERVICE_UPS_GROUND_SAVER_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 7,
  },

  // DHL
  dhl_express: {
    deliveryWindow: 'SERVICE_DHL_EXPRESS_WINDOW',
    description: 'SERVICE_DHL_EXPRESS_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 3,
  },
  dhlecom_ground: {
    deliveryWindow: 'SERVICE_DHLECOM_GROUND_WINDOW',
    description: 'SERVICE_DHLECOM_GROUND_DESC',
    minDeliveryDays: 3,
    maxDeliveryDays: 8,
  },
  dhlecom_expedited: {
    deliveryWindow: 'SERVICE_DHLECOM_EXPEDITED_WINDOW',
    description: 'SERVICE_DHLECOM_EXPEDITED_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 5,
  },
  dhlecom_expedited_max: {
    deliveryWindow: 'SERVICE_DHLECOM_EXPEDITED_MAX_WINDOW',
    description: 'SERVICE_DHLECOM_EXPEDITED_MAX_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 4,
  },

  // FedEx
  fedex_2day_onerate: {
    deliveryWindow: 'SERVICE_FEDEX_2DAY_ONERATE_WINDOW',
    description: 'SERVICE_FEDEX_2DAY_ONERATE_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 2,
  },
  fedex_2day: {
    deliveryWindow: 'SERVICE_FEDEX_2DAY_WINDOW',
    description: 'SERVICE_FEDEX_2DAY_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 2,
  },
  // Standard Overnight delivers by 3-5 pm — rank 2
  fedex_standard_overnight: {
    deliveryWindow: 'SERVICE_FEDEX_STANDARD_OVERNIGHT_WINDOW',
    description: 'SERVICE_FEDEX_STANDARD_OVERNIGHT_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 1,
    deliveryTimeRank: 2,
  },
  // Priority Overnight delivers by 10:30 am — rank 1 to beat Standard Overnight
  fedex_priority_overnight: {
    deliveryWindow: 'SERVICE_FEDEX_PRIORITY_OVERNIGHT_WINDOW',
    description: 'SERVICE_FEDEX_PRIORITY_OVERNIGHT_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 1,
    deliveryTimeRank: 1,
  },
  fedex_ground: {
    deliveryWindow: 'SERVICE_FEDEX_GROUND_WINDOW',
    description: 'SERVICE_FEDEX_GROUND_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 5,
  },
  fedex_home_delivery: {
    deliveryWindow: 'SERVICE_FEDEX_HOME_DELIVERY_WINDOW',
    description: 'SERVICE_FEDEX_HOME_DELIVERY_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 5,
  },
  fedex_economy: {
    deliveryWindow: 'SERVICE_FEDEX_ECONOMY_WINDOW',
    description: 'SERVICE_FEDEX_ECONOMY_DESC',
    minDeliveryDays: 3,
    maxDeliveryDays: 7,
  },
  fedex_international_economy: {
    deliveryWindow: 'SERVICE_FEDEX_INTERNATIONAL_ECONOMY_WINDOW',
    description: 'SERVICE_FEDEX_INTERNATIONAL_ECONOMY_DESC',
    minDeliveryDays: 4,
    maxDeliveryDays: 6,
  },
  fedex_international_priority: {
    deliveryWindow: 'SERVICE_FEDEX_INTERNATIONAL_PRIORITY_WINDOW',
    description: 'SERVICE_FEDEX_INTERNATIONAL_PRIORITY_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 3,
  },

  // OnTrac
  ontrac_ground: {
    deliveryWindow: 'SERVICE_ONTRAC_GROUND_WINDOW',
    description: 'SERVICE_ONTRAC_GROUND_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 3,
  },

  // Amazon
  amazon_ground: {
    deliveryWindow: 'SERVICE_AMAZON_GROUND_WINDOW',
    description: 'SERVICE_AMAZON_GROUND_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 5,
  },

  // Uni Uni
  uniuni_ground: {
    deliveryWindow: 'SERVICE_UNIUNI_GROUND_WINDOW',
    description: 'SERVICE_UNIUNI_GROUND_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 8,
  },

  // Gofo
  gofo_ground: {
    deliveryWindow: 'SERVICE_GOFO_GROUND_WINDOW',
    description: 'SERVICE_GOFO_GROUND_DESC',
    minDeliveryDays: 2,
    maxDeliveryDays: 8,
  },

  // Horizon
  horizon_ground: {
    deliveryWindow: 'SERVICE_HORIZON_GROUND_WINDOW',
    description: 'SERVICE_HORIZON_GROUND_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 6,
  },

  // Cainiao
  // Display says "Next day (China) / 20-50 days (intl)" — min/max here are ranking-only
  // (min=1 for domestic China next-day, max=50 for international worst case)
  cainiao_ground: {
    deliveryWindow: 'SERVICE_CAINIAO_GROUND_WINDOW',
    description: 'SERVICE_CAINIAO_GROUND_DESC',
    minDeliveryDays: 1,
    maxDeliveryDays: 50,
  },
}
